import { Checkbox, FormControlLabel } from "@mui/material";
import ConfirmationModal from "@/components/app/ConfirmationModal";
import Flex from "@/components/Flex";
import Text from "@/components/Text";
import useDisclosure from "@/hooks/use-disclosure";
import MultiSelectGroup from "./MultiSelectGroup";
import {
  NESTED_OPTIONS_CLEAR_CONFIRM,
  NESTED_OPTIONS_REQUIRED,
  OPTION_CONTROL_SX,
  OPTION_ROW_SX,
} from "../readmission-f2f-constant";
import { GroupValue, OptionGroupDef } from "../readmission-f2f-types";
import { toggleGroupOption } from "../readmission-f2f-utils";

export interface NestedOptionsGroupProps {
  group: OptionGroupDef;
  nested: NonNullable<OptionGroupDef["nested"]>;
  value: GroupValue;
  onChange: (value: GroupValue) => void;
}

/** Top options as a normal group; the nested options show under the lead and clear, after a confirmation, when it is unticked. */
const NestedOptionsGroup = ({
  group,
  nested,
  value,
  onChange,
}: NestedOptionsGroupProps) => {
  const clearConfirmation = useDisclosure(false);
  const picks = value.selected.filter((it) => nested.options.includes(it));
  const isLeadTicked = value.selected.includes(nested.lead);

  const onTopChange = (next: GroupValue) => {
    const cleared = next.selected.includes(nested.lead)
      ? next
      : {
          ...next,
          selected: next.selected.filter((it) => !nested.options.includes(it)),
        };
    if (cleared !== next && picks.length) clearConfirmation.open(cleared);
    else onChange(cleared);
  };

  return (
    <MultiSelectGroup
      title={group.title}
      options={group.options}
      value={value}
      onChange={onTopChange}
      exclusive={group.exclusive}
      withDetails={false}
      freeTextPlaceholder={group.freeTextPlaceholder}
    >
      {isLeadTicked && (
        <Flex flexWrap="wrap" gap={2} pl={3}>
          {nested.options.map((option) => (
            <FormControlLabel
              key={option}
              sx={OPTION_ROW_SX}
              label={<Text>{option}</Text>}
              control={
                <Checkbox
                  size="small"
                  sx={OPTION_CONTROL_SX}
                  checked={value.selected.includes(option)}
                  onChange={() =>
                    onChange(toggleGroupOption(value, option, group.exclusive))
                  }
                />
              }
            />
          ))}
        </Flex>
      )}
      {isLeadTicked && !picks.length && (
        <Text size={12} c="error.main" pl={3}>
          {NESTED_OPTIONS_REQUIRED}
        </Text>
      )}
      <ConfirmationModal
        disclosureHost={clearConfirmation}
        title={NESTED_OPTIONS_CLEAR_CONFIRM.TITLE}
        content={NESTED_OPTIONS_CLEAR_CONFIRM.CONTENT}
        confirmationText={NESTED_OPTIONS_CLEAR_CONFIRM.CONFIRM}
        onConfirm={() => {
          onChange(clearConfirmation.data);
          clearConfirmation.close();
        }}
      />
    </MultiSelectGroup>
  );
};

export default NestedOptionsGroup;
